import { MajorListSort, ScholarshipSort, SortDirection } from '@common-types';
import type { SortByOption } from '@pages/Scholarships/components/SortBy';

export const STATE_PUBLIC_COLLEGE_FILTER = 'statePublicCollege';

export const REST_OF_US_FILTER = 'restOfUs';

export const getStatePublicCollegeFilter = (filters: Record<string, unknown>) =>
  (filters[STATE_PUBLIC_COLLEGE_FILTER] as string[] | undefined)?.[0];

export const getRestOfUsFilter = (filters: Record<string, unknown>) =>
  !!(filters[REST_OF_US_FILTER] as string[] | undefined)?.length;

export const isStatePublicCollegeOnlyFilter = (filters: Record<string, unknown>) =>
  !!getStatePublicCollegeFilter(filters) && !getRestOfUsFilter(filters);

export const getStatePublicCollegesCountLabel = (count: number, total: number) => `${count} of ${total}`;

export const getStateSpotlightLocationParams = (filters: Record<string, unknown>) => ({
  ...(getStatePublicCollegeFilter(filters) && { isUshe: true }),
  ...(getRestOfUsFilter(filters) && { isRestUs: true }),
});

export const REST_OF_US_LABEL = 'All other U.S. colleges';

export enum SchoolListSortOption {
  DISTANCE = 'distance',
  NAME = 'name',
  USHE = 'ushe',
  USHE_DISTANCE = 'usheDistance',
}

export const STATE_SPOTLIGHT_SCHOOL_SORT = {
  sort: SchoolListSortOption.USHE_DISTANCE,
  sortDirection: SortDirection.ASC,
};

export const STATE_SPOTLIGHT_MAJOR_SORT = { sort: MajorListSort.NEAREST_UTAH, sortDirection: SortDirection.ASC };

export const getStatePublicCollegesNearestLabel = (stateName: string) => `${stateName} public colleges, then nearest`;

const SCHOOL_SORT_OPTIONS: SortByOption[] = [
  { value: `${SchoolListSortOption.DISTANCE},${SortDirection.ASC}`, label: 'Distance: Nearest first' },
  { value: `${SchoolListSortOption.NAME},${SortDirection.ASC}`, label: 'Name: A to Z' },
];

export const getSchoolSortOptions = (stateName?: string): SortByOption[] =>
  stateName
    ? [
        {
          value: `${SchoolListSortOption.USHE_DISTANCE},${SortDirection.ASC}`,
          label: getStatePublicCollegesNearestLabel(stateName),
        },
        { value: `${SchoolListSortOption.USHE},${SortDirection.ASC}`, label: `${stateName} public colleges first` },
        ...SCHOOL_SORT_OPTIONS,
      ]
    : SCHOOL_SORT_OPTIONS;

export type MajorListSortOption = MajorListSort;

const MAJOR_SORT_OPTIONS: SortByOption[] = [
  { value: `${MajorListSort.CIP_TITLE},${SortDirection.ASC}`, label: 'Name: A to Z' },
  { value: `${MajorListSort.CIP_TITLE},${SortDirection.DESC}`, label: 'Name: Z to A' },
  { value: `${MajorListSort.BEST_FIT},${SortDirection.ASC}`, label: 'Best fit' },
];

export const getMajorSortOptions = (stateName?: string): SortByOption[] =>
  stateName
    ? [
        {
          value: `${MajorListSort.NEAREST_UTAH},${SortDirection.ASC}`,
          label: getStatePublicCollegesNearestLabel(stateName),
        },
        ...MAJOR_SORT_OPTIONS,
      ]
    : MAJOR_SORT_OPTIONS;

export type ScholarshipSortOption = ScholarshipSort;

export const getStateSpotlightScholarshipSortOptions = (stateName: string): SortByOption[] => [
  { value: `${ScholarshipSort.UTAH},${SortDirection.ASC}`, label: `${stateName} scholarships first` },
  { value: `${ScholarshipSort.DEADLINE},${SortDirection.ASC}`, label: 'Deadline: Earliest to Latest' },
  { value: `${ScholarshipSort.AWARD},${SortDirection.DESC}`, label: 'Award: Highest first' },
];

export const STATE_PUBLIC_COLLEGES_SUBTITLE = 'Universities, community and technical colleges';

export const getStatePublicCollegesLabel = (stateName: string) => `${stateName} public colleges`;

export const getStatePublicCollegeBadgeLabel = (stateName: string) => `${stateName} public college`;

export const getStatePublicCollegesNotice = (stateName: string) =>
  `${stateName}'s public colleges are shown first. Add all other U.S. colleges, or filter by state, anytime.`;

export const getExploreStatePublicCollegesLabel = (stateName: string, count: number) =>
  `Explore ${stateName}'s ${count} public colleges`;

export const STATE_APPLY_LINKS: Record<string, { label: string; url: string }> = {
  UT: { label: 'Apply with Admit Utah', url: 'https://admitutah.org/' },
};

export const STATE_SPOTLIGHT_BREADCRUMB_ROOT = 'Explore';

export const STATE_INSTITUTION_SCHOLARSHIPS_LIMIT = 20;

export const USHE_GROUPED_SCHOOLS_LIMIT = 30;

export const USHE_GROUPED_SCHOOLS_VISIBLE = 6;
